import React, { useState } from 'react';
import Icon from 'components/AppIcon';

export default function DataMappingTools() {
  const [sourceFields] = useState([
    'Chemical_Name',
    'CAS_Number',
    'Material_Type',
    'Compatibility_Rating',
    'Temperature_C',
    'Concentration_Percent',
    'Test_Method',
    'Reference_Source',
  ]);

  const [targetFields] = useState([
    'chemical_name',
    'cas_number',
    'material_name',
    'compatibility_rating',
    'temperature',
    'concentration',
    'test_standard',
    'source_reference',
  ]);

  const [mappings, setMappings] = useState({
    Chemical_Name: 'chemical_name',
    CAS_Number: 'cas_number',
    Material_Type: 'material_name',
    Compatibility_Rating: 'compatibility_rating',
  });

  return (
    <div className="space-y-6">
      {/* Mapping Overview */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Data Field Mapping</h3>
            <p className="text-sm text-muted-foreground">
              Map source fields to target database schema
            </p>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex items-center gap-2">
              <Icon name="RotateCcw" size={18} />
              Reset
            </button>
            <button className="btn-primary flex items-center gap-2">
              <Icon name="Save" size={18} />
              Save Mapping
            </button>
          </div>
        </div>

        {/* Mapping Progress */}
        <div className="mb-6 p-4 bg-accent/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Mapping Progress</span>
            <span className="text-sm text-muted-foreground">
              {Object.keys(mappings)?.length} / {sourceFields?.length} fields mapped
            </span>
          </div>
          <div className="h-2 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${(Object.keys(mappings)?.length / sourceFields?.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
      {/* Field Mapping Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-semibold text-foreground">Source Field</th>
                <th className="text-center py-3 px-4 font-semibold text-foreground">Mapping</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Target Field</th>
                <th className="text-center py-3 px-4 font-semibold text-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {sourceFields?.map((field, index) => (
                <tr key={index} className="border-b border-border hover:bg-accent/50 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Icon name="FileText" size={16} className="text-blue-500" />
                      <code className="text-sm font-medium text-foreground">{field}</code>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Icon name="ArrowRight" size={20} className="text-primary mx-auto" />
                  </td>
                  <td className="py-3 px-4">
                    <select
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                      value={mappings?.[field] || ''}
                      onChange={(e) =>
                        setMappings({ ...mappings, [field]: e?.target?.value })
                      }
                    >
                      <option value="">Select target field...</option>
                      {targetFields?.map((target) => (
                        <option key={target} value={target}>
                          {target}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {mappings?.[field] ? (
                      <span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">
                        Mapped
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 text-xs rounded">
                        Pending
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Data Transformation Rules */}
      <div className="card">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Data Transformation Rules
        </h3>
        <div className="space-y-3">
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-start gap-3">
                <Icon name="Code" size={20} className="text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">Temperature Conversion</h4>
                  <p className="text-sm text-muted-foreground">
                    Convert Fahrenheit to Celsius: (°F - 32) × 5/9
                  </p>
                </div>
              </div>
              <button className="text-sm text-primary hover:underline">Edit</button>
            </div>
          </div>

          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-start gap-3">
                <Icon name="Code" size={20} className="text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">Rating Normalization</h4>
                  <p className="text-sm text-muted-foreground">
                    Map numerical ratings (1-4) to letter grades (A-D)
                  </p>
                </div>
              </div>
              <button className="text-sm text-primary hover:underline">Edit</button>
            </div>
          </div>

          <button className="w-full p-4 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors flex items-center justify-center gap-2 text-muted-foreground hover:text-primary">
            <Icon name="Plus" size={20} />
            Add Transformation Rule
          </button>
        </div>
      </div>
      {/* Validation Rules */}
      <div className="card bg-blue-500/5 border border-blue-500/20">
        <div className="flex gap-3">
          <Icon name="Shield" size={20} className="text-blue-500 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">Data Validation</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• CAS numbers must follow standard format (XXXX-XX-X)</li>
              <li>• Temperature values must be within -200°C to 500°C range</li>
              <li>• Compatibility ratings must be A, B, C, or D</li>
              <li>• Concentration must be between 0% and 100%</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}